import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import HomePage from './components/Homepage';
import LobbyPage from './components/LobbyPage';
import GamePage from './components/GamePage';
import RulesPage from './components/RulesPage';
import FAQPage from './components/FAQPage';
import PrivacyPage from './components/PrivacyPage';
import ClosurePage from './components/ClosurePage';
import CookieConsent from './components/CookieConsent';
import InstallBanner from './components/InstallBanner';
import { LanguageProvider } from './contexts/LanguageContext';
import { PWAProvider } from './contexts/PWAContext';
import { AudioProvider } from './contexts/AudioContext';
import { WebVitals } from './components/WebVitals';
import logger from './utils/logger';
import socket, { getGameContext, clearGameContext } from './utils/socket';

// Configure server URL globally - use port 4001 since that's what the server is using
const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';
localStorage.setItem('server_url', SERVER_URL);
logger.info('App using server URL:', SERVER_URL);

// Create a wrapper component to handle navigation
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Rehydrate game state from localStorage on mount so a browser refresh keeps the
  // player in their game/lobby instead of dumping them on the homepage. setGameContext
  // (utils/socket.js) writes {gameId, playerName, screen} with a 1-hour TTL; getGameContext
  // returns null when expired. Server-side reconnect-by-name (joinGameSession /
  // joinGameRoom) then restores the live state once the matching component mounts.
  // Functional initializer (not useEffect) avoids a home->game render ping-pong.
  const [gameState, setGameState] = useState(() => {
    const ctx = getGameContext();
    if (ctx && ctx.gameId && ctx.playerName && (ctx.screen === 'lobby' || ctx.screen === 'game')) {
      return {
        screen: ctx.screen,
        gameId: ctx.gameId,
        playerName: ctx.playerName,
        gameType: 'friends',
        maxPlayers: 4,
        gameData: null,
        error: null
      };
    }
    return {
      screen: 'home',
      gameId: '',
      playerName: '',
      gameType: '',
      maxPlayers: 4,
      gameData: null,
      error: null
    };
  });

  // Add error handling state
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Connection status tracking
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Version tracking for auto-refresh on new deployments
  const [appVersion, setAppVersion] = useState(null);

  // Site closure kill-switch (driven by runtime flag in /site-status.json)
  const [siteClosed, setSiteClosed] = useState(false);

  // Update URL when game state changes
  useEffect(() => {
    if (gameState.screen === 'lobby' || gameState.screen === 'game') {
      // Only navigate if we're not already on the game page
      if (location.pathname !== '/game') {
        logger.debug(`Navigating to /game for screen: ${gameState.screen}`);
        navigate('/game');
      }
    } else if (gameState.screen === 'home' && location.pathname !== '/' && location.pathname !== '/rules' && location.pathname !== '/faq' && location.pathname !== '/privacy') {
      logger.debug(`Navigating to / for home screen`);
      navigate('/');
    }
  }, [gameState.screen, navigate, location.pathname]);

  // Socket connection status monitoring
  useEffect(() => {
    const handleConnect = () => {
      logger.info('Socket connected');
      setConnectionStatus('connected');
      setReconnectAttempt(0);
    };

    const handleDisconnect = (reason) => {
      logger.warn('Socket disconnected:', reason);
      setConnectionStatus('disconnected');
    };

    const handleReconnecting = (attemptNumber) => {
      logger.info('Socket reconnecting, attempt:', attemptNumber);
      setConnectionStatus('reconnecting');
      setReconnectAttempt(attemptNumber);
    };

    const handleReconnect = (attemptNumber) => {
      logger.info('Socket reconnected after', attemptNumber, 'attempts');
      setConnectionStatus('connected');
      setReconnectAttempt(0);
    };

    const handleReconnectFailed = () => {
      logger.error('Socket reconnection failed');
      setConnectionStatus('failed');
      setError('Unable to connect to server. Please check your internet connection.');
    };

    // Set up listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('reconnecting', handleReconnecting);
    socket.on('reconnect', handleReconnect);
    socket.on('reconnect_failed', handleReconnectFailed);

    // Set initial status
    if (socket.connected) {
      setConnectionStatus('connected');
    }

    // Cleanup
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('reconnecting', handleReconnecting);
      socket.off('reconnect', handleReconnect);
      socket.off('reconnect_failed', handleReconnectFailed);
    };
  }, []);

  // Version check for auto-refresh on new deployments
  useEffect(() => {
    const checkVersion = async () => {
      try {
        // Add cache-busting parameter to ensure fresh fetch
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (!response.ok) return;

        const data = await response.json();
        const serverVersion = data.version;

        if (appVersion === null) {
          // First load - just store the version
          setAppVersion(serverVersion);
          logger.debug('App version initialized:', serverVersion);
        } else if (appVersion !== serverVersion) {
          // Version mismatch detected
          logger.info('New app version detected:', serverVersion, '(current:', appVersion, ')');

          // Only auto-refresh if user is on home screen (not in active game/lobby)
          if (gameState.screen === 'home') {
            logger.info('Auto-refreshing to load new version...');
            window.location.reload();
          } else {
            logger.info('User in game/lobby - skipping auto-refresh');
          }
        }
      } catch (err) {
        // Silently fail - version check is not critical
        logger.debug('Version check failed:', err.message);
      }
    };

    // Check version on initial load
    checkVersion();

    // Check version when app returns to foreground
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [appVersion, gameState.screen]);

  // Site closure kill-switch: poll a static runtime flag so the game can be
  // taken offline (or brought back) without a code change. Fails OPEN — if the
  // flag can't be fetched, the game stays available.
  useEffect(() => {
    let cancelled = false;

    const checkSiteStatus = async () => {
      try {
        const response = await fetch(`/site-status.json?t=${Date.now()}`);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        setSiteClosed(!!data.closed);
      } catch (err) {
        // Silently fail - keep whatever state we already have (fail open)
        logger.debug('Site status check failed:', err.message);
      }
    };

    // Check on initial load, on tab foreground, and every 60s
    checkSiteStatus();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSiteStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    const intervalId = setInterval(checkSiteStatus, 60000);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, []);

  // Define handleBackToHome with useCallback before using it in useEffect
  const handleBackToHome = useCallback(() => {
    try {
      logger.info('Returning to home screen from lobby');
      // Clear the persisted context so a subsequent refresh doesn't restore the
      // user back into the game/lobby they just left.
      clearGameContext();
      setGameState({
        screen: 'home',
        gameId: '',
        playerName: '',
        gameType: '',
        maxPlayers: 4,
        gameData: null
      });
    } catch (err) {
      logger.error('Error returning to home:', err);
      setError(`Failed to return to home: ${err.message}`);
    }
  }, []);

  // Add an effect to log screen changes and monitor for issues
  useEffect(() => {
    logger.debug(`Screen changed to: ${gameState.screen}`);
    logger.debug('Current game state:', gameState);
    
    // Set a safety timeout to make sure we never get stuck on loading
    if (gameState.screen !== 'home') {
      const safetyTimer = setTimeout(() => {
        if (document.documentElement.textContent.trim() === '') {
          logger.error('Possible white screen detected, forcing re-render');
          // Force a re-render by updating error state
          setError('Screen render timeout - recovering');
          
          // After a brief pause to allow error to be shown, retry rendering the component
          setTimeout(() => {
            setError(null);
            // If we're still having issues, return to home as fallback
            if (document.documentElement.textContent.trim() === '') {
              handleBackToHome();
            }
          }, 500);
        }
      }, 2000);
      
      return () => clearTimeout(safetyTimer);
    }
  }, [gameState, gameState.screen, handleBackToHome]);

  const handleJoinGame = (gameId, playerName, gameType, maxPlayers = 4) => {
    try {
      logger.info(`Joining game ${gameId} as ${playerName} (${gameType})`);
      setIsLoading(true);
      setError(null);
      
      // Apply a short timeout to ensure UI updates properly before screen change
      setTimeout(() => {
        setGameState({
          screen: 'lobby',
          gameId,
          playerName,
          gameType,
          maxPlayers: parseInt(maxPlayers, 10) || 4,
          gameData: null
        });
        
        // Add another small delay before turning off loading
        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      }, 300);
      
    } catch (err) {
      logger.error('Error joining game:', err);
      setError(`Failed to join game: ${err.message}`);
      setIsLoading(false);
    }
  };

  const handleUpdatePlayerName = (newName) => {
    setGameState(prevState => ({
      ...prevState,
      playerName: newName
    }));
  };

  const handleStartGame = (gameData = null) => {
    try {
      logger.info('Starting game with data:', gameData);
      setIsLoading(true);
      setError(null);
      
      // Apply a short timeout to ensure UI updates properly before screen change
      setTimeout(() => {
        setGameState(prevState => ({
          ...prevState,
          screen: 'game',
          gameData
        }));
        
        // Add another small delay before turning off loading
        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      }, 300);
      
    } catch (err) {
      logger.error('Error starting game:', err);
      setError(`Failed to start game: ${err.message}`);
      setIsLoading(false);
    }
  };

  const handleEndGame = () => {
    try {
      logger.info('Ending game, returning to home');
      // Clear the persisted context so a refresh after "Back to home" / quit
      // doesn't restore the user into the finished game.
      clearGameContext();
      setGameState({
        screen: 'home',
        gameId: '',
        playerName: '',
        gameType: '',
        maxPlayers: 4,
        gameData: null
      });
    } catch (err) {
      logger.error('Error ending game:', err);
      setError(`Failed to end game: ${err.message}`);
    }
  };

  // Global kill-switch: when the site is closed, show only the closure page
  // for every route and screen.
  if (siteClosed) {
    return <ClosurePage />;
  }

  return (
    <div className="App">
      {/* Connection status banners */}
      {connectionStatus === 'reconnecting' && (
        <div className="reconnection-banner">
          <div className="reconnection-content">
            <div className="reconnection-spinner"></div>
            <span>🔄 Reconnecting to server... (Attempt {reconnectAttempt}/8)</span>
          </div>
        </div>
      )}

      {connectionStatus === 'disconnected' && (
        <div className="disconnection-banner">
          <span>❌ Connection lost. Reconnecting...</span>
        </div>
      )}

      {connectionStatus === 'failed' && (
        <div className="connection-failed-banner">
          <span>❌ Unable to connect to server. Please check your internet connection.</span>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      
      <Routes>
        <Route path="/rules" element={<RulesPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route
          path="/game"
          element={
            gameState.screen === 'game' ? (
              <GamePage 
                gameId={gameState.gameId}
                playerName={gameState.playerName}
                gameType={gameState.gameType}
                gameData={gameState.gameData}
                onEndGame={handleEndGame}
              />
            ) : gameState.screen === 'lobby' ? (
              <LobbyPage 
                key={`lobby-${gameState.gameId}`}
                gameId={gameState.gameId}
                playerName={gameState.playerName}
                gameType={gameState.gameType}
                onStartGame={handleStartGame}
                onLeaveLobby={handleBackToHome}
                initialMaxPlayers={gameState.maxPlayers}
                onUpdatePlayerName={handleUpdatePlayerName}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route 
          path="/"
          element={
            <HomePage onJoinGame={handleJoinGame} />
          }
        />
      </Routes>
      
      {/* Fallback if no screen matches (should never happen but prevents white screen) */}
      {!['home', 'lobby', 'game'].includes(gameState.screen) && (
        <div className="fallback-container">
          <h1>Oops! Something went wrong</h1>
          <p>We couldn't load the correct screen. Please try going back to the home page.</p>
          <button className="fallback-button" onClick={handleBackToHome}>
            Return to Home
          </button>
        </div>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <WebVitals />
      <LanguageProvider>
        <AudioProvider>
          <PWAProvider>
            <Router>
              <AppContent />
              <InstallBanner />
            </Router>
          </PWAProvider>
        </AudioProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
